/**
 * Candela Nav — sidebar drawer (mobile), search overlay, cart triggers,
 * announcement bar dismiss.
 *
 * Reads `data-search-url` from #CandelaSearchOverlay so we don't need any
 * Liquid templating in this file.
 */
(function () {
  /* ------------------------------------------------------------------
     Focus trap utility
     ------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var activeTrap = null;

  function trapFocus(container) {
    releaseFocus();
    var handler = function (e) {
      if (e.key !== 'Tab') return;
      var focusable = Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (focusable.length === 0) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    activeTrap = handler;
  }

  function releaseFocus() {
    if (activeTrap) {
      document.removeEventListener('keydown', activeTrap);
      activeTrap = null;
    }
  }

  /* ------------------------------------------------------------------
     Init — called on load and on shopify:section:load
     ------------------------------------------------------------------ */
  function initNav(scope) {
    var root = scope || document;
    var menuBtn = root.querySelector('.candela-nav__menu-btn');
    var closeBtn = root.querySelector('.candela-nav__close-btn');
    var sidebar = root.querySelector('#CandelaNavDrawer') || document.getElementById('CandelaNavDrawer');
    var overlay = root.querySelector('.candela-nav__overlay') || document.querySelector('.candela-nav__overlay');
    if (!sidebar) return;

    var isDesktop = function () {
      return window.matchMedia('(min-width: 990px)').matches;
    };

    function lockScroll() {
      if (isDesktop()) return;
      document.body.style.overflow = 'hidden';
    }

    function unlockScroll() {
      document.body.style.overflow = '';
    }

    function open() {
      sidebar.classList.add('is-open');
      sidebar.setAttribute('aria-modal', 'true');
      if (overlay) overlay.classList.add('is-visible');
      if (menuBtn) menuBtn.setAttribute('aria-expanded', 'true');
      lockScroll();
      trapFocus(sidebar);
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      sidebar.classList.remove('is-open');
      sidebar.removeAttribute('aria-modal');
      if (overlay) overlay.classList.remove('is-visible');
      if (menuBtn) menuBtn.setAttribute('aria-expanded', 'false');
      unlockScroll();
      releaseFocus();
      if (menuBtn) menuBtn.focus();
    }

    if (menuBtn) menuBtn.addEventListener('click', function () {
      if (sidebar.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);

    /* Announcement bar dismiss */
    var announcement = document.getElementById('CandelaAnnouncement');
    var annCloseBtn = announcement ? announcement.querySelector('.candela-nav__announcement-close') : null;
    if (annCloseBtn) {
      annCloseBtn.addEventListener('click', function () {
        announcement.remove();
        document.documentElement.style.setProperty('--candela-announcement-h', '0rem');
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sidebar.classList.contains('is-open')) {
        close();
      }
    });

    /* Search overlay */
    var searchOverlay = document.getElementById('CandelaSearchOverlay');
    var searchUrl = searchOverlay ? searchOverlay.dataset.searchUrl : '/search';
    var searchDialog = searchOverlay ? searchOverlay.querySelector('.candela-search-overlay__dialog') : null;
    var searchTriggers = root.querySelectorAll('.candela-nav__search-trigger');
    var searchInput = searchOverlay ? searchOverlay.querySelector('.candela-search-overlay__input') : null;
    var searchCloseBtn = searchOverlay ? searchOverlay.querySelector('.candela-search-overlay__close') : null;
    var lastSearchTrigger = null;

    function openSearch(trigger) {
      if (!searchOverlay) return;
      lastSearchTrigger = trigger || null;
      searchOverlay.classList.add('is-active');
      searchOverlay.setAttribute('aria-hidden', 'false');
      lockScroll();
      if (window.CandelaScrollLock) window.CandelaScrollLock.update();
      if (searchDialog) trapFocus(searchDialog);
      if (searchInput) {
        searchInput.value = '';
        try { searchInput.focus({ preventScroll: true }); } catch (e) { searchInput.focus(); }
      }
    }

    function closeSearch() {
      if (!searchOverlay) return;
      searchOverlay.classList.remove('is-active');
      searchOverlay.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (window.CandelaScrollLock) window.CandelaScrollLock.update();
      releaseFocus();
      if (lastSearchTrigger) {
        try { lastSearchTrigger.focus({ preventScroll: true }); } catch (e) { lastSearchTrigger.focus(); }
      }
    }

    searchTriggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (sidebar.classList.contains('is-open')) close();
        openSearch(btn);
      });
    });

    if (searchCloseBtn) searchCloseBtn.addEventListener('click', closeSearch);

    if (searchOverlay) {
      searchOverlay.addEventListener('click', closeSearch);
      if (searchDialog) searchDialog.addEventListener('click', function (e) { e.stopPropagation(); });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && searchOverlay && searchOverlay.classList.contains('is-active')) {
        closeSearch();
      }
    });

    /* Intercept all search links sitewide (footer, etc.) */
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/search"]');
      if (link && link.getAttribute('href').replace(/\/$/, '') === searchUrl.replace(/\/$/, '')) {
        e.preventDefault();
        openSearch(link);
      }
    });

    /* Cart drawer triggers */
    var cartTriggers = root.querySelectorAll('.candela-nav__cart-trigger');
    cartTriggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (sidebar.classList.contains('is-open')) close();
        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      });
    });
  }

  /* Run on load */
  initNav();

  /* Re-init when Theme Editor reloads the section */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('.candela-nav')) {
      initNav(e.target);
    }
  });
})();
